// ── Renders Badlands tombstones (PvP death drops) as interactable world markers. ──
// The realm room only replicates `tombstoneIds` (no positions — the authoritative pos lives
// in apps/api). The world scene therefore tells us where to place a marker:
//   • our own drop is placed at our death position (captured from the `died` message);
//   • other players' tombstones (ids we have not seen before) are placed near the local
//     player so they remain reachable/lootable.
// Looting is server-validated; we just send `lootTombstone` and reconcile on the next
// tombstoneIds snapshot. Degrades gracefully: no ids → nothing rendered.

import type { Vec2 } from '@mn/shared';

const TOMBSTONE_DEPTH = 1;
const LOOT_RANGE = 96; // px — how close the player must be to loot

interface Marker {
  id: string;
  x: number;
  y: number;
  gfx: Phaser.GameObjects.Container;
}

export class Tombstones {
  #scene: Phaser.Scene;
  #byId = new Map<string, Marker>();

  constructor(scene: Phaser.Scene) {
    this.#scene = scene;
  }

  /** Whether a tombstone with this id is already rendered. */
  has(id: string): boolean {
    return this.#byId.has(id);
  }

  /**
   * Reconcile rendered markers against the authoritative id list. New ids are placed at
   * `fallbackNear` (slightly scattered) so they are visible + lootable; removed ids vanish.
   */
  sync(ids: string[], fallbackNear: Vec2): void {
    const live = new Set(ids);
    // remove looted / expired
    for (const id of [...this.#byId.keys()]) {
      if (!live.has(id)) this.remove(id);
    }
    // add newcomers near the player (no server-side position is replicated)
    let n = 0;
    for (const id of ids) {
      if (this.#byId.has(id)) continue;
      const offset = 48 + n * 24;
      this.place(id, { x: fallbackNear.x + offset, y: fallbackNear.y });
      n += 1;
    }
  }

  /** Place (or reposition) a tombstone marker at an exact world position. */
  place(id: string, pos: Vec2): void {
    const existing = this.#byId.get(id);
    if (existing) {
      existing.x = pos.x;
      existing.y = pos.y;
      existing.gfx.setPosition(pos.x, pos.y);
      return;
    }
    const stone = this.#scene.add.rectangle(0, 0, 28, 36, 0x5a5f6e, 0.95).setStrokeStyle(3, 0x9aa0b0, 1);
    const cross = this.#scene.add.text(0, -4, '✝', { fontSize: '20px', color: '#e8e8f0' }).setOrigin(0.5);
    const tag = this.#scene.add
      .text(0, -30, 'LOOT', { fontSize: '12px', color: '#ffd34d', backgroundColor: '#00000080' })
      .setOrigin(0.5);
    const container = this.#scene.add.container(pos.x, pos.y, [stone, cross, tag]).setDepth(TOMBSTONE_DEPTH);
    this.#byId.set(id, { id, x: pos.x, y: pos.y, gfx: container });
  }

  /** Return the nearest tombstone within loot range of the given position, if any. */
  nearest(x: number, y: number): { id: string; dist: number } | null {
    let best: { id: string; dist: number } | null = null;
    for (const m of this.#byId.values()) {
      const dist = Math.hypot(m.x - x, m.y - y);
      if (dist <= LOOT_RANGE && (!best || dist < best.dist)) best = { id: m.id, dist };
    }
    return best;
  }

  remove(id: string): void {
    const m = this.#byId.get(id);
    if (!m) return;
    m.gfx.destroy();
    this.#byId.delete(id);
  }

  clear(): void {
    for (const id of [...this.#byId.keys()]) this.remove(id);
  }
}
